import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_SAVED_PROPERTIES } from '../../../lib/graphql/queries/contacts';
import type { GQLSavedPropertyItem } from './useContact';

const DEFAULT_PAGE_SIZE = 25;

interface GetContactSavedPropertiesData {
  contact: {
    contactId: string;
    savedProperties: {
      items: GQLSavedPropertyItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactSavedPropertiesGQL = (contactId: string, pageSize = DEFAULT_PAGE_SIZE) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactSavedPropertiesData>(
    GET_CONTACT_SAVED_PROPERTIES,
    {
      variables: {
        organizationId: organization?.id ?? '',
        contactId,
        limit: pageSize,
        offset: currentPage * pageSize,
        sortOrder: 'DESC',
      },
      skip: !organization?.id || !contactId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const items = data?.contact?.savedProperties?.items ?? [];
  const queryTotal = data?.contact?.savedProperties?.total;
  const hasMore = data?.contact?.savedProperties?.hasMore ?? false;

  // Changing pages changes Apollo variables and can clear `data`. Preserve the
  // last completed total for this exact tenant/contact so the shared counter
  // remains stable during pagination and after a failed page request.
  const totalScope = `${organization?.id ?? ''}:${contactId}`;
  const stableTotal = useRef<{ scope: string; value: number } | null>(null);
  if (!loading && queryTotal !== undefined) {
    stableTotal.current = { scope: totalScope, value: queryTotal };
  }
  const total = stableTotal.current?.scope === totalScope ? stableTotal.current.value : 0;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return { items, total, hasMore, loading, error, hasNext: hasMore, handleNextPage, resetPage };
};
